import React from 'react';
import ReactDOM from 'react-dom';
import { BiLoaderCircle } from 'react-icons/bi';
import { Icons } from '@/utils/Icons';
import CustomInput from '@/components/common/CustomInput';
import ChangePasswordModal from '@/components/modals/ChangePasswordModal';
import { useLoginPage } from '@/pages/Auth/hooks/useLogin';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const {
    form,
    handleLogin,
    handleDemoLogin,
    isLoading,
    showChangePasswordModal,
    setShowChangePasswordModal,
  } = useLoginPage({ onSuccess: onClose });

  const { control, handleSubmit } = form;

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[400px] rounded-[10px] border-[3px] border-[#ff9500]  p-[14px] text-xs shadow-2xl bg-white dark:bg-[#18162A]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-black dark:text-white transition hover:text-red-500"
        >
          <Icons.Cross2 size={12} />
        </button>

        {/* Header */}
        <h2 className="mb-3 text-center text-base font-semibold dark:text-white text-black">
          Login
        </h2>
        <div className="mb-6 border-b-2 border-black dark:border-white" />

        <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
          <CustomInput
            name="username"
            control={control}
            // label="User ID"
            type="text"
            placeholder="User ID"
          />

          <CustomInput
            name="password"
            control={control}
            // label="Password"
            type="password"
            placeholder="Password"
          />

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              type="submit"
              disabled={isLoading}
              className="flex h-11 items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition hover:bg-white hover:text-black disabled:opacity-60 "
            >
              {isLoading ? <BiLoaderCircle className="animate-spin" size={20} /> : 'Login'}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={handleDemoLogin}
              className="flex h-11 items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition hover:bg-white hover:text-black disabled:opacity-60"
            >
              Demo ID
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="my-4 border-b border-dashed border-newLoginHeader" />

        {/* Social Section */}
        <div className="text-center">
          <p className="mb-4 text-[12px] text-black dark:text-white">Connect with us</p>
          <div className="flex flex-wrap justify-center gap-1">
            {['whatsapp', 'telegram', 'facebook', 'insta', 'twitter', 'youtube'].map((social) => (
              <button
                key={social}
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-black bg-[#FCFCFC] hover:bg-[#EBBE71]"
              >
                <img src={`/images/social/${social}.png`} alt={social} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        setShowChangePasswordModal={setShowChangePasswordModal}
      />
    </div>,
    document.body
  );
};

export default LoginModal;
